/**
 * Actor schemas (D232, actors round A) — an embodied thing with a location
 * that can act between the player's turns (`docs/design/actors.md` §1–§3;
 * Mike's rulings of 2026-10-04 at the top of its §9).
 *
 * @remarks
 * **An actor is a NEW entity kind, not an item with facets** (ruling 1). An
 * item is a thing the world holds still for the player; the whole point of
 * an actor is that it does not. Bolting a location and a voice onto items
 * would make every item handler ask "but is it a person?" — the reach gate,
 * the take verb, the container graph, the acquisition record. Illegal states
 * unrepresentable instead: an actor cannot be carried because it is not an
 * item, and the engine's item verbs refuse it by construction, in its own
 * words (`verbNarratives`) or the `verbOnActor` slot's.
 *
 * What round A gives an actor, and why each field is shaped as it is:
 *
 * - **Reference** — `name`, `nouns`, `aliases`, `adjectives`, exactly as
 *   items declare them, so the parser's catalog carries an actor as it
 *   carries any noun and never learns the word "actor".
 * - **A place** — `initialRoomId`, optional. Absent, the actor starts
 *   OFFSTAGE: declared, nowhere, waiting for round C's `move_actor`. That is
 *   how a thing arrives later without a flag pretending it was always there
 *   (`actors-content.md` §2: the drain animal arrives with the gas; a
 *   `visibleWhen` that fakes the arrival pops rather than arrives). In round
 *   A nothing moves an actor, so the loader refuses one with no
 *   `initialRoomId` as dead content until C lands.
 * - **Two answers to "is she here"** (Calder's §5.2 — one condition was
 *   doing two jobs). `audibleFrom` names the rooms an actor may be ADDRESSED
 *   from without being REACHED: the occupant behind the steel door answers
 *   `ask her about the film` and refuses `attack her` with the door's
 *   reason, not with "she is not here". Reach defaults to co-location, so an
 *   actor that is simply in the room declares nothing. The split is the one
 *   the item model already makes between what the catalog can name and what
 *   the reach gate lets you touch (D19a); the conditions `actor_present`
 *   (reachable) and `actor_addressable` (speakable-to) read the two answers.
 * - **A voice** — `speakerId`, a speaker the pack declares (D192). An
 *   actor's line is authored with that speaker as its `source`, like every
 *   other line in the pack, so the Voices index, the cadence, the color and
 *   the quotation law all come from the speaker layer unchanged. What the
 *   body adds is Calder's §6 ruling: a line spoken by an actor's voice
 *   carries the speaker's name as REAL TEXT in the entry — the transcript's
 *   substitute for looking at them — while a disembodied voice (EURYDICE)
 *   keeps its bare quotation marks. One speaker is at most one actor's
 *   voice: the label names a body, and two bodies under one name would lie.
 *   A mute actor (the drain animal) declares none.
 * - **Conversation** — `greetingNarratives` (what `talk to <actor>` hears),
 *   `topics` and `topicFallbackNarratives` (D231's table, verbatim), and
 *   `askNarratives` (what is said ABOUT the actor when something else is
 *   asked — the one-topic case items and rooms carry).
 * - **Refusal** — `verbNarratives`, keyed by canonical verb, for the verbs
 *   an actor refuses in its own words; `unreachableNarratives` for the verbs
 *   refused because the body is out of reach (requires `audibleFrom`, or the
 *   lines could never speak — the D47 family's rule).
 *
 * - **Movement** (D233, round C; `actors.md` §5). An actor's location
 *   changes only through the `move_actor` effect — from offstage, between
 *   rooms, to offstage — and the move is a fact in `world.actorLocations`
 *   that a replay reproduces and a checkpoint rewinds (where a body stands
 *   is furniture; what it has said is the player's). `arrivalNarratives` and
 *   `departureNarratives` are what the player hears when they SEE the move —
 *   the actor entering or leaving the room they stand in; an unwitnessed move
 *   is silent, which is how "one room behind you" works. `blocksExits` names
 *   the exits this body stands in the way of while it is present: sugar the
 *   loader rewrites into each exit's lock as a `blockedWhen: actor_present`
 *   hold (the general primitive — a lock any condition can hold, which
 *   nothing in the unlock ledger cuts), speaking `blockingNarratives`. A door
 *   a guard stands in front of and a door that needs a keycard are the same
 *   lock with different keys.
 *
 * - **Combat** (D234, round D; `actors.md` §6). `combat` makes the body a
 *   FOE: health, what it does to the player while `hostileWhen` holds and it
 *   is within reach, what it says as it strikes, is struck and falls, and
 *   what its fall does (`combat.ts` has the whole shape). A body with no
 *   `combat` is not a foe and refuses `attack` in its own words; a body with
 *   one is attackable whether or not it is hostile now (ruling 3 — the
 *   loader has proved its hostility CAN come true), so `verbNarratives` may
 *   not key `attack` on it: the strike lands, and the line could never
 *   speak. Health and defeat are the world's for the checkpoint (ruling 4).
 *
 * - **Prompts** (D235, round B2; `actors.md` §4, Mike's ruling 6). `prompts`
 *   are the questions this actor can ASK — each a parked question of the
 *   `dialogue` kind with visible answers (`prompts.ts` has the whole shape).
 *   `opensWith` is the sugar door: `talk to <actor>` speaks the greeting and
 *   then asks that prompt, if its `when`-gated options leave anything to
 *   ask; the `ask_prompt` effect is the general door, a trigger making the
 *   actor speak first. A prompt is spoken in the actor's voice, so an actor
 *   with prompts declares a `speakerId` — a mute body cannot ask.
 * - **Gifts** (D235). `giveInteractions` is what happens when the player
 *   hands this body a thing (`give`, `hand`, `offer`; `show` reads the same
 *   table and keeps the thing in the hands) — the `use` interaction's shape
 *   with `consumesItem`. The refusal ladder is `use`'s: not carried; no arm
 *   for the thing (`refusesGiftsNarratives`, else the `giftRefused` slot);
 *   conditions unmet. Gifts and speech pass where the hands do not: a body
 *   heard through a door can be offered a thing and refuse it in its own
 *   words, which is the occupant's whole register.
 *
 * - **Following** (D238; `actor-follows.ts` has the whole shape). `follows`
 *   makes the body go where the player goes while its `while` holds —
 *   silently, moved before the room's page is read, so the page can say it
 *   is there — and stop at the rooms it `refuses`, in its own words. It
 *   stores nothing: the follow writes `world.actorLocations`, the record
 *   `move_actor` writes. A follower cannot also stand in the way
 *   (`blocksExits`) or fight (`combat`) — see that module for why.
 *
 * - **Pockets** (D240; `containment.ts`). A thing whose `containedIn`
 *   names this body as `containerActorId` is carried by it — referable
 *   while the body is present, moved with it, found by `search <actor>` when
 *   concealed. `searchNarratives` answer the search that finds nothing.
 * Not here, on purpose: `patrol`
 * (sugar over a self-scheduling event chain of `move_actor`s, deferred until
 * content has a beat to walk — the drain animal does not follow), `tell
 * <actor> about <X>` (`ask` with the arrow reversed over the same table —
 * recorded as the first extension, no content asks for it). `pronoun` has
 * its reader now, and only this much of one: the conversational partner
 * (the body the player last addressed, or that last asked the player) is
 * what its own pronoun means while it is still within earshot; everything
 * else resolves by recency as it always has.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { actorFollowsSchema } from './actor-follows.js';
import { actorCombatSchema } from './combat.js';
import { conditionalTextSchema, descriptionFragmentSchema } from './conditional-text.js';
import { conditionSchema } from './conditions.js';
import { actorIdSchema, exitIdSchema, promptIdSchema, roomIdSchema, speakerIdSchema } from './identifiers.js';
import { unsupportedVerbNarrativesSchema } from './items.js';
import { nonBlankStringSchema } from './primitives.js';
import { promptsSchema } from './prompts.js';
import { topicsSchema } from './topics.js';
import { narrativeLinesSchema } from './transcript.js';
import { giveInteractionSchema } from './use-interactions.js';
import { verbInteractionsSchema } from './verb-interactions.js';

/** One authored actor — see the module remarks. */
/** D235: the pronoun a body is spoken of by — the subject form; the parser's object forms (`him`, `her`, `it`, `them`) follow from it. */
export const actorPronounSchema = z.enum(['he', 'she', 'it', 'they']);

/** The pronoun a body is spoken of by (D235). */
export type ActorPronoun = z.infer<typeof actorPronounSchema>;

export const actorDefinitionSchema = z
	.strictObject({
		actorId: actorIdSchema,
		adjectives: z.array(nonBlankStringSchema).readonly().default([]),
		aliases: z.array(nonBlankStringSchema).readonly().default([]),
		/** D233: what the player sees when this actor ENTERS the room they stand in — spoken by the witnessed `move_actor`, under `actor_moved:<actorId>`. Unwitnessed arrivals are silent. */
		arrivalNarratives: narrativeLinesSchema.optional(),
		/** What is said ABOUT this actor when something else is asked about them (`ask the voice about the woman`) — the one-topic case. Absence is silence. */
		askNarratives: narrativeLinesSchema.optional(),
		/**
		 * The rooms this actor can be SPOKEN TO from without being reached —
		 * behind a door, across glass, at the far end of a line. From these
		 * rooms the actor is in the catalog and answers `ask`/`talk`; every
		 * other verb refuses with `unreachableNarratives` or the
		 * `actorOutOfReach` slot. The actor's own room needs no listing: reach
		 * implies earshot.
		 */
		audibleFrom: z.array(roomIdSchema).readonly().default([]),
		/**
		 * D233: what the way says while this body is in it — the refusal every
		 * exit in `blocksExits` speaks while the actor is present. Requires
		 * `blocksExits` (or the lines could never speak), and `blocksExits`
		 * requires it: the sugar carries its own words, because a desugared
		 * lock is an ordinary lock, and an ordinary lock speaks its refusal.
		 */
		blockingNarratives: narrativeLinesSchema.optional(),
		/**
		 * D233: the exits this actor stands in the way of while it is PRESENT
		 * (in the player's room and visible). Each must be an exit of a room
		 * the actor can stand in — its `initialRoomId` or a `move_actor`
		 * destination; the loader says so. Desugared at load into each exit's
		 * lock as `blockedWhen: { actor_present }` with `blockingNarratives`
		 * as the first refusal variant; the engine never learns the word.
		 */
		blocksExits: z.array(exitIdSchema).readonly().default([]),
		/** D234: what this body is as a FOE — see `combat.ts`. Absent, it is not one, and `attack` refuses. */
		combat: actorCombatSchema.optional(),
		/** D233: what the player sees when this actor LEAVES the room they stand in — spoken by the witnessed `move_actor`. */
		departureNarratives: narrativeLinesSchema.optional(),
		/** What the player sees — the environment's line on `examine`, a plain string or conditional text (D55). */
		description: conditionalTextSchema,
		/** D140: sentences appended to the description while their conditions hold. */
		descriptionFragments: z.array(descriptionFragmentSchema).readonly().default([]),
		/** D238: a body that goes where the player goes — see `actor-follows.ts`. Absent, it goes only where a `move_actor` sends it. */
		follows: actorFollowsSchema.optional(),
		/** D235: what happens when a thing is handed to this body (`give`; `show` reads it too) — see `use-interactions.ts`. */
		giveInteractions: z.array(giveInteractionSchema).readonly().default([]),
		/** What the actor says when addressed about nothing in particular — `talk to <actor>`, `ask <actor>`. Absent, the `askDeflection` slot: silence is authored silence. */
		greetingNarratives: narrativeLinesSchema.optional(),
		/** Where the actor starts. Absent, offstage — declared, nowhere, waiting to be moved in (round C). */
		initialRoomId: roomIdSchema.optional(),
		name: nonBlankStringSchema,
		/** D182: whether the engine's lines may put "the" before the name — `false` for a proper noun. A name that already opens with "the" never takes a second one. */
		nameTakesArticle: z.boolean().default(true),
		nouns: z.array(nonBlankStringSchema).min(1).readonly(),
		/** D235: the prompt `talk to <actor>` asks after the greeting, when its options leave anything to ask. One of this actor's `prompts`. */
		opensWith: promptIdSchema.optional(),
		/**
		 * D252: said when the actor is given an order no `orders` arm answers —
		 * the floor under its orders, as `topicFallbackNarratives` is under its
		 * topics. Absent, the `orderRefused` slot speaks.
		 */
		orderFallbackNarratives: narrativeLinesSchema.optional(),
		/**
		 * D252: what this body does when it is TOLD to do something — `tell
		 * TALLY to follow me`, `TALLY, follow me`. Keyed by the canonical verb
		 * of the clause it was told, and read as `verbInteractions` are: arms in
		 * authored order, the first whose conditions hold speaks and acts, the
		 * ordered clause's things in hand (`tool_is` and its twins). Any declared
		 * verb may be a key, the engine's own and the unsupported among them:
		 * the player cannot `follow` on their own, and TALLY can be told to.
		 */
		orders: verbInteractionsSchema.optional(),
		/** D235: the questions this actor can ask — see `prompts.ts`. Requires a `speakerId`: a question is spoken in the actor's voice. */
		prompts: promptsSchema.optional(),
		/**
		 * D235: the pronoun the player uses for this body — `she` is answered
		 * to as `her`, `he` as `him`, `they` as `them`, `it` as `it`. Read by
		 * the conversational-partner rule: while this actor is the one the
		 * player is talking with and still within earshot, that pronoun names
		 * it ahead of recency ("ask her about the film" after she asked). An
		 * actor without one is reached by its nouns alone, and the pronoun
		 * resolves as it always has.
		 */
		pronoun: actorPronounSchema.optional(),
		/** D235: said when a thing is offered that no `giveInteractions` arm takes. Absent, the `giftRefused` slot. */
		refusesGiftsNarratives: narrativeLinesSchema.optional(),
		/**
		 * D240: what SEARCHING this body says when the search turns up nothing —
		 * the item field of the same name (D50), on a body. A body that carries
		 * something (a thing whose `containedIn.containerActorId` names it) is
		 * gone through by `search <actor>`, its concealed contents found as a
		 * container's are; these lines answer the search that finds nothing, or
		 * nothing more. On a body that carries nothing they ARE the search — the
		 * guard patted down — and without them, `search` on such a body refuses
		 * as every item verb on a body does.
		 */
		searchNarratives: narrativeLinesSchema.optional(),
		/** The declared speaker this actor speaks as (D192). Absent, the actor is mute: it has no lines of its own and no entry in Voices. */
		speakerId: speakerIdSchema.optional(),
		/** D231: said when the actor is addressed about a subject no topic answers. Requires `topics` (the loader says so, as for items). */
		topicFallbackNarratives: narrativeLinesSchema.optional(),
		/** D231: the keyed answer table — `ask <actor> about <X>`. */
		topics: topicsSchema.optional(),
		/** What refusing a verb says while the actor is heard but not reached (`audibleFrom`). Absent, the `actorOutOfReach` slot. Requires `audibleFrom`. */
		unreachableNarratives: narrativeLinesSchema.optional(),
		/**
		 * D251: what a pack's own verb DOES to this body — D196's table, read
		 * exactly as a thing's is: canonical verb → arms in authored order, the
		 * first whose conditions hold speaks and acts. `release tally` was the
		 * case: the drone is clamped, the player's band releases it, and the
		 * act belongs to TALLY rather than to the dock beside it. Keys follow
		 * the item rule (a supported verb of the pack's own, by its canonical
		 * token); `content-loader` owns it.
		 */
		verbInteractions: verbInteractionsSchema.optional(),
		/**
		 * What THIS actor says to a verb aimed at it, keyed by canonical verb
		 * token (D47's shape). Every item verb refuses on an actor — `take`,
		 * `open`, `use`, the pack's own verbs — so unlike an item's
		 * `unsupportedVerbNarratives` a key may name a mechanized verb; only
		 * the verbs an actor ANSWERS (`ask` and its aliases, `inspect`) may
		 * not be keyed, because they never reach a refusal. Absent, the
		 * `verbOnActor` slot speaks. D251: and when the body has arms for the
		 * verb (`verbInteractions`) and none of them holds, these lines are
		 * still its refusal — arms add the act, they do not take the words.
		 */
		verbNarratives: unsupportedVerbNarrativesSchema.optional(),
		/** The item rule verbatim: unmet, the actor is not referable at all. */
		visibleWhen: conditionSchema.optional(),
	})
	.readonly()
	.refine((actor) => actor.unreachableNarratives === undefined || actor.audibleFrom.length > 0, {
		message:
			'unreachableNarratives requires audibleFrom — an actor that is only ever met in its own room is never heard without being reached, so the lines could never speak',
		path: ['unreachableNarratives'],
	})
	.refine((actor) => actor.initialRoomId === undefined || !actor.audibleFrom.includes(actor.initialRoomId), {
		message: 'audibleFrom lists the actor\'s own initialRoomId — reach already implies earshot there; list only the rooms the actor is heard from without being reached',
		path: ['audibleFrom'],
	})
	// D233: the sugar and its words come together — neither means anything alone.
	.refine((actor) => (actor.blocksExits.length > 0) === (actor.blockingNarratives !== undefined), {
		message:
			'blocksExits and blockingNarratives come together — the exits this body stands in the way of, and what the way says while it does; one without the other is a lock with no words, or words with no lock',
		path: ['blocksExits'],
	})
	.refine((actor) => new Set(actor.blocksExits).size === actor.blocksExits.length, {
		message: 'blocksExits lists the same exit twice',
		path: ['blocksExits'],
	})
	// D234 (ruling 3): a foe is struck, never refused — the key would be a line that could never speak.
	.refine((actor) => actor.combat === undefined || actor.verbNarratives?.['attack'] === undefined, {
		message:
			'verbNarratives keys `attack` on an actor with a `combat` block — a foe is attackable whether or not it is hostile now, so the strike lands and these lines could never speak; drop the key, or drop `combat` to make the body refuse',
		path: ['verbNarratives', 'attack'],
	})
	// D238: a body that goes everywhere the player goes would stand in every doorway the player stands in.
	.refine((actor) => actor.follows === undefined || actor.blocksExits.length === 0, {
		message:
			'follows beside blocksExits — a follower stands at every door the player stands at, so it would hold every one of them; a following body never blocks the player\'s own way',
		path: ['follows'],
	})
	// D238: D234's promise is that leaving works — a foe that follows strikes in every room.
	.refine((actor) => actor.follows === undefined || actor.combat === undefined, {
		message:
			'follows beside combat — a foe that follows the player is a foe nobody can leave, and leaving the room is how every fight is escaped (D234); the stalker is its own primitive, not yet built',
		path: ['follows'],
	})
	// D240: a body that can be gone through answers `search` itself — a refusal keyed on it could never speak.
	.refine((actor) => actor.searchNarratives === undefined || actor.verbNarratives?.['search'] === undefined, {
		message:
			'verbNarratives keys `search` beside searchNarratives — a body with searchNarratives can be gone through, so the search runs and the refusal could never speak; drop one',
		path: ['verbNarratives', 'search'],
	})
	// D235: a question is spoken in the actor's voice, and a mute body has none to ask in.
	.refine((actor) => actor.prompts === undefined || actor.speakerId !== undefined, {
		message: 'prompts need a speakerId — a prompt\'s question is spoken in the actor\'s own voice, and a mute actor has none to ask in',
		path: ['prompts'],
	})
	// D235: the sugar door names one of this actor's own prompts.
	.refine((actor) => actor.opensWith === undefined || actor.prompts?.some((prompt) => prompt.promptId === actor.opensWith) === true, {
		message: 'opensWith names a prompt this actor does not declare — `talk to <actor>` asks one of its own `prompts`',
		path: ['opensWith'],
	})
	// D235: like topic ids, prompt ids are keys into the player's ledger; one actor declaring two under one id would ask itself.
	.refine((actor) => actor.prompts === undefined || new Set(actor.prompts.map((prompt) => prompt.promptId)).size === actor.prompts.length, {
		message: 'the actor declares the same promptId twice',
		path: ['prompts'],
	});

/** One authored actor — see {@link actorDefinitionSchema}. */
export type ActorDefinition = z.infer<typeof actorDefinitionSchema>;
